import classNames from "classnames";
import Collapse, { CollapseProps } from "rc-collapse";
import "rc-collapse/assets/index.css";
import { ReactNode } from "react";
import type { DraggableProvided, DroppableProvided } from "react-beautiful-dnd";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-collapse-panel.scss";

interface ICollapseMotion {
  motionName: string;
  motionAppear: boolean;
  motionDeadline: number;
}

const COLLAPSE_MOTION_NAME: string = "kuika-collapse-motion";
const DURATION_FALLBACK_MS: number = 200;
// rc-motion ends a step on the content's transitionend; the deadline is only the safety net for a
// transition that never ends (no layout change, a hidden tab), so it trails the duration.
const DEADLINE_GRACE_MS: number = 100;

/** A motion token's duration in milliseconds (`--dur-base: 200ms`), as the root defines it. */
const readDurationToken = (name: string): number => {
  const raw: string = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  let milliseconds: number = Number.NaN;

  if (raw.endsWith("ms")) {
    milliseconds = parseFloat(raw);
  } else if (raw.endsWith("s")) {
    milliseconds = parseFloat(raw) * 1000;
  }

  return Number.isFinite(milliseconds) ? milliseconds : DURATION_FALLBACK_MS;
};

let collapseMotion: ICollapseMotion | null = null;

/**
 * The open/close motion: the content's grid row goes 0fr ↔ 1fr on --dur-base (the classes are in
 * kuika-collapse-panel.scss). Panels open on first render appear without it. Read once — the
 * token does not change at run time.
 */
const getCollapseMotion = (): ICollapseMotion => {
  if (collapseMotion === null) {
    collapseMotion = {
      motionName: COLLAPSE_MOTION_NAME,
      motionAppear: false,
      motionDeadline: readDurationToken("--dur-base") + DEADLINE_GRACE_MS
    };
  }

  return collapseMotion;
};

interface IExpandIconProps {
  isActive?: boolean;
}

/**
 * What react-beautiful-dnd hands a Droppable or a Draggable render function. `innerRef` has to
 * reach a real DOM element, which is why the panel grows a wrapper when it is given one:
 * rc-collapse's own ref is a component, not a node, so rbd never learned where the folder was and
 * nothing could be dropped into it.
 */
type CollapseDndProvided = DroppableProvided | DraggableProvided;

interface KuikaCollapsePanelProps extends CollapseProps {
  items?: CollapseProps["items"];
  activeKey?: CollapseProps["activeKey"];
  defaultActiveKey?: CollapseProps["defaultActiveKey"];
  onChange?: CollapseProps["onChange"];
  expandIcon?: (panelProps: any) => ReactNode;
  provided?: CollapseDndProvided;
  forceRender?: boolean;
  expandIconPosition?: "left" | "right";
  iconPlacement?: "left" | "right";
  /**
   * Opens and closes the content with a grid-row height motion (--dur-base). On by default; under
   * reduced motion the panel opens and closes at once. Panels open on first render never animate.
   */
  animated?: boolean;
}

const KuikaCollapsePanel: React.FC<KuikaCollapsePanelProps> = ({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  expandIcon,
  provided,
  forceRender,
  expandIconPosition = "left",
  iconPlacement = "left",
  animated = true,
  // Pulled out of `restProps`: it is spread onto `Collapse` below, and a `className` left
  // in it overwrote the computed one — a caller passing a layout class silently dropped
  // `kuika-collapse-panel` and with it every style the component has.
  className,
  ...restProps
}) => {
  const prefersReducedMotion: boolean = usePrefersReducedMotion();
  // Two names for one thing. `expandIconPosition` is antd's spelling and five call sites use it;
  // it used to be forwarded to rc-collapse, which has no such prop, so those panels asked for a
  // right-hand chevron and silently kept a left one. The class below is what actually moves it.
  const isIconOnRight: boolean = iconPlacement === "right" || expandIconPosition === "right";
  // One chevron that turns as the panel opens, rather than two glyphs swapped at once.
  const defaultExpandIcon = (panelProps: IExpandIconProps): ReactNode => (
    <KuikaSvg
      width={16}
      height={16}
      type="arrows"
      name="chevron-right"
      className={classNames("kuika-collapse-panel__chevron", {
        "kuika-collapse-panel__chevron--open": panelProps.isActive
      })}
    />
  );

  const panel: JSX.Element = (
    <Collapse
      className={classNames("kuika-collapse-panel", className, {
        "kuika-collapse-panel--icon-right": isIconOnRight,
        "kuika-collapse-panel--animated": animated
      })}
      items={items
        ?.filter((item) => item)
        .map((item) => ({
          ...item,
          // The whole header row is the hover host, so a clipped folder name slides while the
          // row's hover-only controls (the three-dot menu) are showing too.
          headerClass: classNames(item.headerClass, OVERFLOW_REVEAL_HOST_CLASS),
          label: typeof item.label === "string" ? <KuikaOverflowText>{item.label}</KuikaOverflowText> : item.label,
          forceRender: forceRender ?? false
        }))}
      activeKey={activeKey}
      defaultActiveKey={defaultActiveKey}
      onChange={onChange}
      expandIcon={expandIcon || defaultExpandIcon}
      {...restProps}
      // Reduced motion: no motion at all — rc-motion then opens and closes at once.
      openMotion={animated && !prefersReducedMotion ? getCollapseMotion() : undefined}
    />
  );

  if (provided === undefined || provided === null) {
    return panel;
  }

  // The wrapper exists only for drag and drop: it is the element rbd measures and registers. Panels
  // outside a drag context render exactly as before, with no extra box in the tree.
  const setDndRef = (element: HTMLDivElement | null): void => {
    provided.innerRef(element);
  };

  return (
    <>
      <div
        ref={setDndRef}
        className="kuika-collapse-panel__dnd"
        {...("droppableProps" in provided ? provided.droppableProps : {})}
        {...("draggableProps" in provided ? provided.draggableProps : {})}
        {...("dragHandleProps" in provided ? provided.dragHandleProps : {})}
      >
        {panel}
      </div>
      {"placeholder" in provided ? provided.placeholder : null}
    </>
  );
};

export default KuikaCollapsePanel;
